import { logger } from '@lib/logging-system';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { disconnectEcho, getEchoInstance, reconnectEcho } from '@/config/echo';
import type {
  WebSocketAnnouncement,
  WebSocketChannel,
  WebSocketConnectionState,
  WebSocketDeliveryUpdate,
  WebSocketError,
  WebSocketMessage,
  WebSocketMessagePriority,
  WebSocketMetrics,
  WebSocketNotification,
  WebSocketQueueItem,
} from '@/interfaces/WebSocketInterfaces';
import type { AppState } from '@/store/appStore';

import { meLogOut } from './meReducer';
import { addErrorNotification } from './notificationReducer';

// Helper function to generate unique message ID
const generateMessageId = (): string => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to check if message should be queued
const shouldQueueMessage = (priority: WebSocketMessagePriority): boolean =>
  priority === 'high' || priority === 'urgent';

// Helper function to get retry delay based on attempts
const getRetryDelay = (attempts: number): number => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 60000; // 1 minute
  const delay = Math.min(baseDelay * 2 ** attempts, maxDelay);
  return delay;
};

// WebSocket state interface
export interface WebSocketState {
  // Connection state
  connectionState: WebSocketConnectionState;
  connectionError: WebSocketError | null;

  // Channels
  channels: Record<string, WebSocketChannel>;

  // Messages
  messages: WebSocketMessage[];
  messageHistory: Record<string, WebSocketMessage>;
  messageQueue: WebSocketQueueItem[];

  // Notifications
  notifications: WebSocketNotification[];
  unreadNotificationCount: number;

  // Delivery updates
  activeDeliveryUpdates: Record<string, WebSocketDeliveryUpdate>;

  // Announcements
  announcements: WebSocketAnnouncement[];

  // Metrics
  metrics: WebSocketMetrics;

  // Settings
  autoReconnect: boolean;
  maxHistorySize: number;
  queueEnabled: boolean;
}

// Initial state
const initialState: WebSocketState = {
  connectionState: 'disconnected',
  connectionError: null,
  channels: {},
  messages: [],
  messageHistory: {},
  messageQueue: [],
  notifications: [],
  unreadNotificationCount: 0,
  activeDeliveryUpdates: {},
  announcements: [],
  metrics: {
    reconnectCount: 0,
    messagesSent: 0,
    messagesReceived: 0,
  },
  autoReconnect: true,
  maxHistorySize: 1000,
  queueEnabled: true,
};

// Create slice
export const webSocketSlice = createSlice({
  name: 'webSocket',
  initialState,
  reducers: {
    // Connection state actions
    setConnectionState: (state, action: PayloadAction<WebSocketConnectionState>) => {
      state.connectionState = action.payload;

      if (action.payload === 'connected') {
        state.connectionError = null;
        state.metrics.connectedAt = new Date().toISOString();
        state.metrics.disconnectedAt = undefined;
      } else if (action.payload === 'disconnected') {
        state.metrics.disconnectedAt = new Date().toISOString();
      }
    },

    setConnectionError: (state, action: PayloadAction<WebSocketError>) => {
      state.connectionState = 'error';
      state.connectionError = action.payload;
    },

    // Channel actions
    addChannel: (state, action: PayloadAction<WebSocketChannel>) => {
      state.channels[action.payload.name] = action.payload;
    },

    removeChannel: (state, action: PayloadAction<string>) => {
      delete state.channels[action.payload];
    },

    updateChannelStatus: (state, action: PayloadAction<{ name: string; subscribed: boolean }>) => {
      if (state.channels[action.payload.name]) {
        state.channels[action.payload.name].subscribed = action.payload.subscribed;
        if (action.payload.subscribed) {
          state.channels[action.payload.name].subscribedAt = new Date().toISOString();
        }
      }
    },

    // Message actions
    addMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      const message = action.payload;

      // Add to messages array
      state.messages.unshift(message);

      // Limit array size
      if (state.messages.length > 100) {
        state.messages = state.messages.slice(0, 100);
      }

      // Add to history
      state.messageHistory[message.id] = message;

      // Clean up old history if needed
      const historyKeys = Object.keys(state.messageHistory);
      if (historyKeys.length > state.maxHistorySize) {
        const keysToRemove = historyKeys
          .sort((a, b) => {
            const timeA = state.messageHistory[a].timestamp;
            const timeB = state.messageHistory[b].timestamp;
            return timeA.localeCompare(timeB);
          })
          .slice(0, historyKeys.length - state.maxHistorySize);

        keysToRemove.forEach((key) => delete state.messageHistory[key]);
      }

      // Update metrics
      state.metrics.messagesReceived++;

      // Update channel activity
      if (message.channel && state.channels[message.channel]) {
        state.channels[message.channel].lastActivity = message.timestamp;
      }
    },

    // Notification actions
    addNotification: (state, action: PayloadAction<WebSocketNotification>) => {
      state.notifications.unshift(action.payload);
      state.unreadNotificationCount++;

      // Limit notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    markNotificationsRead: (state) => {
      state.unreadNotificationCount = 0;
    },

    clearNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },

    // Delivery update actions
    updateDelivery: (state, action: PayloadAction<WebSocketDeliveryUpdate>) => {
      const update = action.payload;
      state.activeDeliveryUpdates[update.data.delivery_uuid] = update;
    },

    removeDeliveryUpdate: (state, action: PayloadAction<string>) => {
      delete state.activeDeliveryUpdates[action.payload];
    },

    // Announcement actions
    addAnnouncement: (state, action: PayloadAction<WebSocketAnnouncement>) => {
      state.announcements.unshift(action.payload);

      // Remove expired announcements
      const now = new Date().toISOString();
      state.announcements = state.announcements.filter((a) => !a.data.expires_at || a.data.expires_at > now);
    },

    removeAnnouncement: (state, action: PayloadAction<string>) => {
      state.announcements = state.announcements.filter((a) => a.id !== action.payload);
    },

    // Queue actions
    addToQueue: (state, action: PayloadAction<WebSocketMessage>) => {
      if (state.queueEnabled && shouldQueueMessage(action.payload.priority)) {
        state.messageQueue.push({
          message: action.payload,
          attempts: 0,
        });
      }
    },

    removeFromQueue: (state, action: PayloadAction<string>) => {
      state.messageQueue = state.messageQueue.filter((item) => item.message.id !== action.payload);
    },

    incrementQueueAttempt: (state, action: PayloadAction<string>) => {
      const item = state.messageQueue.find((item) => item.message.id === action.payload);
      if (item) {
        item.attempts++;
        const delay = getRetryDelay(item.attempts);
        item.nextRetry = new Date(Date.now() + delay).toISOString();
      }
    },

    clearQueue: (state) => {
      state.messageQueue = [];
    },

    // Metrics actions
    updateMetrics: (state, action: PayloadAction<Partial<WebSocketMetrics>>) => {
      state.metrics = { ...state.metrics, ...action.payload };
    },

    incrementReconnectCount: (state) => {
      state.metrics.reconnectCount++;
    },

    // Settings actions
    setAutoReconnect: (state, action: PayloadAction<boolean>) => {
      state.autoReconnect = action.payload;
    },

    setQueueEnabled: (state, action: PayloadAction<boolean>) => {
      state.queueEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(connectWebSocket.pending, (state) => {
        state.connectionState = 'connecting';
      })
      .addCase(connectWebSocket.fulfilled, (state) => {
        state.connectionState = 'connected';
      })
      .addCase(connectWebSocket.rejected, (state, action) => {
        state.connectionState = 'error';
        state.connectionError = {
          code: 'CONNECTION_FAILED',
          message: action.error.message || 'Failed to connect to WebSocket',
          timestamp: new Date().toISOString(),
        };
      })
      .addCase(disconnectWebSocket.fulfilled, (state) => {
        state.connectionState = 'disconnected';
        state.channels = {};
      })
      .addCase(subscribeToChannel.fulfilled, (state, action) => {
        const { name, type } = action.payload;
        state.channels[name] = {
          name,
          type,
          subscribed: true,
          subscribedAt: new Date().toISOString(),
        };
      })
      .addCase(unsubscribeFromChannel.fulfilled, (state, action) => {
        delete state.channels[action.payload];
      });
  },
});

// Export actions
export const {
  setConnectionState,
  setConnectionError,
  addChannel,
  removeChannel,
  updateChannelStatus,
  addMessage,
  addNotification,
  markNotificationsRead,
  clearNotification,
  updateDelivery,
  removeDeliveryUpdate,
  addAnnouncement,
  removeAnnouncement,
  addToQueue,
  removeFromQueue,
  incrementQueueAttempt,
  clearQueue,
  updateMetrics,
  incrementReconnectCount,
  setAutoReconnect,
  setQueueEnabled,
} = webSocketSlice.actions;

// Thunks
export const connectWebSocket = createAsyncThunk(
  'webSocket/connect',
  async (token: string | null = null, { dispatch }) => {
    const echo = reconnectEcho(token);

    if (!echo) {
      throw new Error('Failed to create Echo instance');
    }

    // Set up global event listeners
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pusher = (echo as any).connector?.pusher;

    pusher?.connection.bind('connected', () => {
      dispatch(setConnectionState('connected'));
      dispatch(processMessageQueue());
    });

    pusher?.connection.bind('disconnected', () => {
      dispatch(setConnectionState('disconnected'));
    });

    pusher?.connection.bind('error', (error: { error?: { code?: string; message?: string } }) => {
      dispatch(
        setConnectionError({
          code: error.error?.code || 'UNKNOWN_ERROR',
          message: error.error?.message || 'WebSocket error occurred',
          timestamp: new Date().toISOString(),
          details: error,
        }),
      );
    });

    pusher?.connection.bind('state_change', (states: { previous: string; current: string }) => {
      logger.info('[webSocketReducer]', 'WebSocket state change:', states.previous, '->', states.current);

      if (states.current === 'connecting') {
        dispatch(setConnectionState('connecting'));
      } else if (states.current === 'unavailable') {
        dispatch(setConnectionState('error'));
      }
    });

    return true;
  },
);

export const disconnectWebSocket = createAsyncThunk('webSocket/disconnect', async (_, { dispatch }) => {
  disconnectEcho();
  dispatch(setConnectionState('disconnected'));
  return true;
});

export const subscribeToChannel = createAsyncThunk(
  'webSocket/subscribeToChannel',
  async ({ name, type }: { name: string; type: 'public' | 'private' | 'presence' }, { dispatch }) => {
    const echo = getEchoInstance();
    if (!echo) {
      throw new Error('WebSocket not connected');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any;

    switch (type) {
      case 'private':
        channel = echo.private(name);
        break;
      case 'presence':
        channel = echo.join(name);
        break;
      default:
        channel = echo.channel(name);
    }

    // Set up channel event listeners
    channel.subscribed(() => {
      dispatch(updateChannelStatus({ name, subscribed: true }));
    });

    channel.error((error: unknown) => {
      console.error(`Channel ${name} error:`, error);
      dispatch(updateChannelStatus({ name, subscribed: false }));
    });

    return { name, type };
  },
);

export const unsubscribeFromChannel = createAsyncThunk(
  'webSocket/unsubscribeFromChannel',
  async (channelName: string) => {
    const echo = getEchoInstance();
    if (echo) {
      echo.leave(channelName);
    }
    return channelName;
  },
);

export const sendWebSocketMessage = createAsyncThunk(
  'webSocket/sendMessage',
  async (message: Omit<WebSocketMessage, 'id' | 'timestamp'>, { dispatch, getState }) => {
    const state = getState() as AppState;
    const { connectionState } = state.webSocket;

    const fullMessage: WebSocketMessage = {
      ...message,
      id: generateMessageId(),
      timestamp: new Date().toISOString(),
    };

    if (connectionState !== 'connected') {
      // Queue message if high priority
      dispatch(addToQueue(fullMessage));
      throw new Error('WebSocket not connected');
    }

    // In a real implementation, you would send this through the WebSocket
    // For now, we'll just update metrics
    dispatch(updateMetrics({ messagesSent: state.webSocket.metrics.messagesSent + 1 }));

    return fullMessage;
  },
);

export const processMessageQueue = createAsyncThunk('webSocket/processQueue', async (_, { dispatch, getState }) => {
  const state = getState() as AppState;
  const { messageQueue, connectionState } = state.webSocket;

  if (connectionState !== 'connected' || messageQueue.length === 0) {
    return;
  }

  const now = new Date().toISOString();
  const readyMessages = messageQueue.filter((item) => !item.nextRetry || item.nextRetry <= now);

  for (const item of readyMessages) {
    try {
      await dispatch(sendWebSocketMessage(item.message));
      dispatch(removeFromQueue(item.message.id));
    } catch {
      dispatch(incrementQueueAttempt(item.message.id));

      // Remove from queue if too many attempts
      if (item.attempts >= 5) {
        dispatch(removeFromQueue(item.message.id));
        dispatch(
          addErrorNotification({
            title: 'Message Failed',
            message: `Failed to send message after ${item.attempts} attempts`,
          }),
        );
      }
    }
  }
});

// Selectors
export const selectWebSocketState = (state: AppState) => state.webSocket;
export const selectConnectionState = (state: AppState) => state.webSocket.connectionState;
export const selectIsConnected = (state: AppState) => state.webSocket.connectionState === 'connected';
export const selectChannels = (state: AppState) => state.webSocket.channels;
export const selectMessages = (state: AppState) => state.webSocket.messages;
export const selectNotifications = (state: AppState) => state.webSocket.notifications;
export const selectUnreadNotificationCount = (state: AppState) => state.webSocket.unreadNotificationCount;
export const selectActiveDeliveryUpdates = (state: AppState) => state.webSocket.activeDeliveryUpdates;
export const selectAnnouncements = (state: AppState) => state.webSocket.announcements;
export const selectMessageQueue = (state: AppState) => state.webSocket.messageQueue;
export const selectMetrics = (state: AppState) => state.webSocket.metrics;

// Export reducer
export default webSocketSlice.reducer;
